import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Mail, Phone, Building, MapPin, Percent, Clock, CheckCircle, Loader2, Edit, Save, X, Trash2, History } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { user, isAuthenticated, requestDiscount, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [discountAmount, setDiscountAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    company_name: '',
    // Transport/Checkout preferences
    gst_number: '',
    private_marka: '',
    preferred_transporter: '',
    preferred_courier: ''
  });
  
  // OE Discount state — per OEM brand (Eicher + Swaraj Mazda).
  // 2026-06-06: split the single legacy "OE discount" into two so
  // customers can carry different deals for each OEM catalog.
  const [oeDiscountEicher, setOeDiscountEicher] = useState('');
  const [currentOeDiscountEicher, setCurrentOeDiscountEicher] = useState(0);
  const [oeDiscountMazda, setOeDiscountMazda] = useState('');
  const [currentOeDiscountMazda, setCurrentOeDiscountMazda] = useState(0);
  const [savingOeBrand, setSavingOeBrand] = useState(null); // 'Eicher' | 'Swaraj Mazda' | null

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, navigate]);

  React.useEffect(() => {
    if (user) {
      setEditForm({
        name: user.name || '',
        email: user.email || '',
        company_name: user.company_name || '',
        gst_number: user.gst_number || '',
        private_marka: user.private_marka || '',
        preferred_transporter: user.preferred_transporter || user.last_transporter || '',
        preferred_courier: user.preferred_courier || ''
      });
      
      // Load current OE discounts (per brand).
      if (token) {
        axios.get(`${API}/customer/oe-discount`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => {
            const d = res.data?.discounts || {};
            const eicher = d.Eicher ?? res.data?.oe_discount_percentage ?? 0;
            const mazda = d['Swaraj Mazda'] ?? 0;
            setCurrentOeDiscountEicher(eicher);
            setOeDiscountEicher(eicher ? String(eicher) : '');
            setCurrentOeDiscountMazda(mazda);
            setOeDiscountMazda(mazda ? String(mazda) : '');
          })
          .catch(() => {});
      }
    }
  }, [user, token]);

  const handleSaveOeDiscount = async (brand) => {
    const value = brand === 'Swaraj Mazda' ? oeDiscountMazda : oeDiscountEicher;
    const discount = parseFloat(value);
    if (isNaN(discount) || discount < 0 || discount > 100) {
      toast.error('Please enter a valid discount between 0 and 100');
      return;
    }

    setSavingOeBrand(brand);
    try {
      await axios.post(`${API}/customer/oe-discount`,
        { discount_percentage: discount, brand },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (brand === 'Swaraj Mazda') setCurrentOeDiscountMazda(discount);
      else setCurrentOeDiscountEicher(discount);
      toast.success(`${brand} OE discount set to ${discount}%`);
    } catch (error) {
      toast.error(`Failed to save ${brand} OE discount`);
    } finally {
      setSavingOeBrand(null);
    }
  };

  const handleRequestDiscount = async (e) => {
    e.preventDefault();
    
    const discount = parseFloat(discountAmount);
    if (isNaN(discount) || discount <= 0 || discount > 50) {
      return;
    }

    setLoading(true);
    await requestDiscount(discount);
    setLoading(false);
    setDiscountAmount('');
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Profile updated successfully');
        setEditing(false);
        // Refresh user data
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditForm({
      name: user?.name || '',
      email: user?.email || '',
      company_name: user?.company_name || '',
      gst_number: user?.gst_number || '',
      private_marka: user?.private_marka || '',
      preferred_transporter: user?.preferred_transporter || user?.last_transporter || '',
      preferred_courier: user?.preferred_courier || ''
    });
  };

  // Clear search history for privacy
  const handleClearSearchHistory = async () => {
    if (!window.confirm('Are you sure you want to clear your search history? This will also reset your personalized recommendations.')) {
      return;
    }
    
    setClearingHistory(true);
    try {
      const response = await axios.delete(`${API}/search/clear-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Search history cleared successfully');
      }
    } catch (error) {
      toast.error('Failed to clear search history');
    } finally {
      setClearingHistory(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: '#ffffff' }}>
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-3xl font-heading font-bold mb-8 text-gray-900">My Profile</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-white border border-gray-200">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-gray-900">
                <div className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Profile Information
                </div>
                {!editing ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setEditing(true)}
                    className="border-gray-300 text-gray-700"
                  >
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleCancelEdit}
                      className="border-gray-300 text-gray-700"
                      disabled={saving}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={handleSaveProfile}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      disabled={saving}
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Customer ID</p>
                <p className="font-mono font-semibold text-gray-900">{user.customer_id}</p>
              </div>
              
              {editing ? (
                <>
                  <div>
                    <Label className="text-gray-700">Name *</Label>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      placeholder="Enter your name"
                      className="mt-1 border-gray-300 text-gray-900"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Email</Label>
                    <Input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      placeholder="Enter your email"
                      className="mt-1 border-gray-300 text-gray-900"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Company Name</Label>
                    <Input
                      value={editForm.company_name}
                      onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                      placeholder="Enter company name"
                      className="mt-1 border-gray-300 text-gray-900"
                    />
                  </div>
                  
                  {/* Transport Preferences Section */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h3 className="font-semibold text-gray-800 mb-3">Checkout Preferences</h3>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-gray-700">GST Number</Label>
                        <Input
                          value={editForm.gst_number}
                          onChange={(e) => setEditForm({ ...editForm, gst_number: e.target.value })}
                          placeholder="Enter GST number"
                          className="mt-1 border-gray-300 text-gray-900"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-700">Private Marka</Label>
                        <Input
                          value={editForm.private_marka}
                          onChange={(e) => setEditForm({ ...editForm, private_marka: e.target.value })}
                          placeholder="Your private marka/mark"
                          className="mt-1 border-gray-300 text-gray-900"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-700">
                          Preferred Transporter
                          {user?.state?.toLowerCase() !== 'delhi' && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        <Input
                          value={editForm.preferred_transporter}
                          onChange={(e) => setEditForm({ ...editForm, preferred_transporter: e.target.value })}
                          placeholder="Your preferred transport company"
                          className="mt-1 border-gray-300 text-gray-900"
                        />
                        <p className="text-xs text-gray-500 mt-1">Default transporter for your orders</p>
                      </div>
                      <div>
                        <Label className="text-gray-700">
                          Preferred Courier
                          {user?.state?.toLowerCase() !== 'delhi' && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        <Input
                          value={editForm.preferred_courier}
                          onChange={(e) => setEditForm({ ...editForm, preferred_courier: e.target.value })}
                          placeholder="Your preferred courier for urgent orders"
                          className="mt-1 border-gray-300 text-gray-900"
                        />
                        <p className="text-xs text-gray-500 mt-1">Default courier for urgent orders</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-semibold text-gray-900">{user.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      Email
                    </p>
                    <p className="text-gray-900">{user.email || '-'}</p>
                  </div>
                  {user.company_name && (
                    <div>
                      <p className="text-sm text-gray-500 flex items-center">
                        <Building className="w-4 h-4 mr-1" />
                        Company
                      </p>
                      <p className="text-gray-900">{user.company_name}</p>
                    </div>
                  )}
                </>
              )}
              
              <div>
                <p className="text-sm text-gray-500 flex items-center">
                  <Phone className="w-4 h-4 mr-1" />
                  Mobile
                </p>
                <p className="text-gray-900">
                  {user.country_code ? `+${user.country_code} ` : ''}{user.mobile}
                </p>
                <p className="text-xs text-gray-400 mt-1">Mobile number cannot be changed</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Category</p>
                <Badge className="bg-primary">{user.customer_category}</Badge>
              </div>
              {user.address && (
                <div>
                  <p className="text-sm text-gray-500 flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    Address
                  </p>
                  <p className="text-sm text-gray-900">{user.address}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200">
            <CardHeader>
              <CardTitle className="flex items-center text-gray-900">
                <Percent className="w-5 h-5 mr-2" />
                Discount Status
              </CardTitle>
              <CardDescription className="text-gray-500">Request and manage your discount</CardDescription>
            </CardHeader>
            <CardContent>
              {user.discount_status === 'none' && (
                <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">No discount applied</p>
                  <p className="text-xs text-gray-500">Request a discount below to get special pricing.</p>
                </div>
              )}

              {user.discount_status === 'pending' && (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md mb-6">
                  <div className="flex items-start">
                    <Clock className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                    <div>
                      <p className="font-semibold text-yellow-900">Pending Approval</p>
                      <p className="text-sm text-yellow-800 mt-1">
                        Requested: {user.discount_percentage}%
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {user.discount_status === 'approved' && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-md mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <div>
                      <p className="font-semibold text-green-900">Discount Approved!</p>
                      <p className="text-2xl font-bold text-green-700 mt-2">
                        {user.discount_percentage}%
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {(user.discount_status === 'none' || user.discount_status === 'rejected') && (
                <form onSubmit={handleRequestDiscount} className="space-y-4">
                  <div>
                    <Label htmlFor="discount" className="text-gray-700">Request Discount (%)</Label>
                    <Input
                      id="discount"
                      type="number"
                      min="1"
                      max="50"
                      step="0.1"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      placeholder="Enter 1-50%"
                      required
                      className="mt-1 border-gray-300 text-gray-900"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary hover:bg-primary/90"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Request'
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* OE Discount Card — split per OEM brand (Eicher + Swaraj Mazda). */}
          <Card className="bg-white border border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center text-gray-900">
                <Percent className="w-5 h-5 mr-2 text-amber-600" />
                OE MRP Discount
              </CardTitle>
              <CardDescription className="text-gray-500">
                Set your discount on OE part prices for each OEM catalog. The discount applies when you search OE MRP on the home page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Eicher OE Discount */}
              <div className="rounded-md border border-amber-200 bg-amber-50/40 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-amber-900">Eicher OE Discount</span>
                  <span className="text-sm text-amber-800">Current: <span className="font-bold">{currentOeDiscountEicher}%</span></span>
                </div>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Label htmlFor="oe-discount-eicher" className="text-gray-700">Set Eicher OE Discount (%)</Label>
                    <Input
                      id="oe-discount-eicher"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={oeDiscountEicher}
                      onChange={(e) => setOeDiscountEicher(e.target.value)}
                      placeholder="Enter 0-100%"
                      className="mt-1 border-amber-300 text-gray-900"
                      data-testid="oe-discount-eicher-input"
                    />
                  </div>
                  <Button
                    onClick={() => handleSaveOeDiscount('Eicher')}
                    disabled={savingOeBrand === 'Eicher'}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="save-oe-discount-eicher"
                  >
                    {savingOeBrand === 'Eicher' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Swaraj Mazda OE Discount */}
              <div className="rounded-md border border-amber-200 bg-amber-50/40 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-amber-900">Swaraj Mazda OE Discount</span>
                  <span className="text-sm text-amber-800">Current: <span className="font-bold">{currentOeDiscountMazda}%</span></span>
                </div>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Label htmlFor="oe-discount-mazda" className="text-gray-700">Set Swaraj Mazda OE Discount (%)</Label>
                    <Input
                      id="oe-discount-mazda"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={oeDiscountMazda}
                      onChange={(e) => setOeDiscountMazda(e.target.value)}
                      placeholder="Enter 0-100%"
                      className="mt-1 border-amber-300 text-gray-900"
                      data-testid="oe-discount-mazda-input"
                    />
                  </div>
                  <Button
                    onClick={() => handleSaveOeDiscount('Swaraj Mazda')}
                    disabled={savingOeBrand === 'Swaraj Mazda'}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="save-oe-discount-mazda"
                  >
                    {savingOeBrand === 'Swaraj Mazda' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy & Search History */}
          <Card className="bg-white border border-gray-200">
            <CardHeader>
              <CardTitle className="flex items-center text-gray-900">
                <History className="w-5 h-5 mr-2" />
                Search History & Privacy
              </CardTitle>
              <CardDescription className="text-gray-500">
                Manage your search history and personalization data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 p-4 rounded-md mb-4 border border-blue-200">
                <p className="text-sm text-blue-800">
                  We use your search history to personalize your experience - showing you relevant parts faster based on your preferences.
                </p>
              </div>
              
              <Button
                onClick={handleClearSearchHistory}
                disabled={clearingHistory}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                {clearingHistory ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Search History
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 mt-2">
                This will clear your recent searches and reset personalized recommendations.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8">
          <Button
            onClick={() => navigate('/catalog')}
            variant="outline"
            className="border-gray-300 text-gray-700"
          >
            Browse Catalog
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
